// Action center (Workspace 2, screen 7): the engine's attention items for one engagement, one client or every client you can
// see. A scope switch, five tabs with counts (questions and help, blocked, your approvals, flagged issues, client requests),
// optional filter chips per kind, and a table grouped by audit area (or by client at firm scope): the item with its kind,
// the workpaper, who it waits on, its age and the one action the engine offers (the command's form, or a link).
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AgentPresenceLine } from "../../app/AgentPresence";
import { useApp } from "../../app/context";
import { EmptyState, ErrorState, LoadingRows, PageHeader, Tabs } from "../../components";
import type { AttentionItem, EngagementHeader } from "../../data/types";
import { useLoad } from "../../lib/hooks";
import { TABS, age, groupBy, reportDeadline, subkindLabel, tabCounts, waitsOnMe } from "../../lib/workspace";
import { useSettled, visibleItems } from "../../lib/settled";
import { AttentionIssueChip, ItemAction, SubkindChip, Who, detailHref, useManyWs, useWsAct } from "./common";
import { IssueHistory } from "./Issues";

export type ActionScope = { kind: "engagement"; id: string } | { kind: "client"; clientId: string } | { kind: "firm" };

export function ActionCenter({ scope, embedded = false }: { scope: ActionScope; embedded?: boolean }) {
  const { ds, me } = useApp();
  const { tab: routeTab } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const es = useLoad(() => ds.engagements(), [ds]);
  const all = es.data ?? [];
  const here = scope.kind === "engagement" ? all.find((e) => e.id === scope.id) : undefined;
  const clientId = scope.kind === "client" ? scope.clientId : here?.clientId;
  const due30 = params.get("due") === "30";
  const inScope = (e: EngagementHeader) =>
    (scope.kind === "engagement" ? e.id === scope.id : scope.kind === "client" ? e.clientId === scope.clientId : true)
    && (!due30 || ((d) => !!d && d.days !== null && d.days >= 0 && d.days <= 30)(reportDeadline(e)));
  const engs = all.filter(inScope);
  const ws = useManyWs(engs.map((e) => e.id));
  const { open, drawer } = useWsAct(() => ws.reload());
  const names = new Map(all.map((e) => [e.id, e.clientName]));

  const settled = useSettled();
  const items: AttentionItem[] = visibleItems([...(ws.data?.values() ?? [])].flatMap((x) => x.attention), settled)
    .filter((i) => params.get("mine") !== "1" || waitsOnMe(i, me.personName));
  const counts = tabCounts(items);
  const tab = routeTab === HISTORY || TABS.some((t) => t.id === routeTab) ? routeTab! : (TABS.find((t) => counts[t.id] > 0)?.id ?? "question");
  const inTab = items.filter((i) => i.tab === tab);
  const subkinds = [...new Set(inTab.map((i) => i.subkind))];
  const sub = params.get("subkind");
  const rows = inTab.filter((i) => !sub || i.subkind === sub).sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));
  const byClient = scope.kind === "firm";
  const groups = groupBy(rows, (i) => (byClient ? names.get(i.engagementId) ?? i.engagementId : i.area ?? "General"))
    .sort(([a], [b]) => (a === "General" ? 1 : b === "General" ? -1 : a.localeCompare(b)));

  const base = (k: ActionScope["kind"]) => k === "engagement" && here ? `/e/${here.id}/actions` : k === "client" && clientId ? `/clients/${encodeURIComponent(clientId)}/actions` : "/actions";
  const tabHref = (t: string) => `${base(scope.kind)}/${t}`;
  const scopes: Array<{ k: ActionScope["kind"]; label: string }> = [
    ...(here ? [{ k: "engagement" as const, label: "This engagement" }] : []),
    ...(clientId ? [{ k: "client" as const, label: "This client" }] : []),
    { k: "firm" as const, label: "All clients" },
  ];
  const title = scope.kind === "engagement" ? "Action center" : scope.kind === "client" ? `Action center · ${engs[0]?.clientName ?? clientId}` : "Action center · all clients";

  return (
    <div className={scope.kind === "engagement" ? "space-y-4" : "mx-auto max-w-7xl space-y-4"}>
      {(() => {
        const switcher = (
          <div role="group" aria-label="Scope" className="inline-flex overflow-hidden rounded-md border border-line text-xs">
            {scopes.map((s, i) => (
              <Link key={s.k} to={`${base(s.k)}/${tab}`} aria-current={s.k === scope.kind ? "page" : undefined}
                className={`px-2.5 py-1.5 ${i ? "border-l border-line" : ""} ${s.k === scope.kind ? "bg-brand text-brand-ink" : "bg-raised hover:bg-sunken"}`}>{s.label}</Link>
            ))}
          </div>
        );
        const subtitle = due30 ? "Engagements with a report due in the next 30 days." : "What waits on someone — you, the team, the client or the agent.";
        return embedded ? (
          <div className="flex flex-wrap items-end justify-between gap-2"><div><h2 className="text-sm font-semibold">Action center</h2><p className="text-xs text-muted">{subtitle}</p></div>{switcher}</div>
        ) : <PageHeader title={title} subtitle={subtitle} actions={switcher} />;
      })()}
      {(params.get("mine") === "1" || due30) && (
        <p className="text-xs text-muted">Filtered: {params.get("mine") === "1" ? "waiting on you" : "reports due in 30 days"} · <Link className="text-brand hover:underline" to={tabHref(tab)}>show everything</Link></p>
      )}
      <Tabs label="Action center tabs" idPrefix="ac" value={tab} onChange={(t) => nav(tabHref(t) + (params.toString() ? `?${params}` : ""))}
        tabs={[...TABS.map((t) => ({ id: t.id, label: t.label, count: counts[t.id] ?? 0 })), { id: HISTORY, label: "History" }]} />
      {tab === HISTORY ? (
        <div role="tabpanel" id={`ac-panel-${tab}`} aria-labelledby={`ac-${tab}`}>
          <IssueHistory engagements={engs.map((e) => e.id)} names={names} byClient={byClient} />
        </div>
      ) : (
      <div role="tabpanel" id={`ac-panel-${tab}`} aria-labelledby={`ac-${tab}`} className="space-y-3">
        {tab === "question" && <AgentPresenceLine className="rounded-md border border-line bg-raised px-3 py-2" />}
        {tab === "issue" && <p className="text-xs text-muted">Open an issue for its full explanation. Issues resolved or closed, and how each was addressed, are in <Link className="text-brand hover:underline" to={tabHref(HISTORY)}>Resolved / history</Link>.</p>}
        {subkinds.length > 1 && (
          <div role="group" aria-label="Kind" className="flex flex-wrap gap-1.5">
            {[null, ...subkinds].map((k) => (
              <button key={k ?? "all"} type="button" aria-pressed={(sub ?? null) === k}
                onClick={() => { const p = new URLSearchParams(params); if (k) p.set("subkind", k); else p.delete("subkind"); setParams(p, { replace: true }); }}
                className={`rounded-full border px-2.5 py-1 text-xs ${(sub ?? null) === k ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line bg-raised hover:bg-sunken"}`}>
                {k ? subkindLabel(k) : "All"} <span className="tabular-nums text-muted">{k ? inTab.filter((i) => i.subkind === k).length : inTab.length}</span>
              </button>
            ))}
          </div>
        )}
        {es.error || ws.error ? <ErrorState error={(es.error ?? ws.error)!} onRetry={() => { es.reload(); ws.reload(); }} />
          : !es.data || !ws.data ? <LoadingRows rows={4} label="Loading the action center" />
          : rows.length === 0 ? <EmptyState title="Nothing here right now.">When something waits on a person, the client or the agent, it shows in this tab.</EmptyState>
          : groups.map(([g, list]) => (
            <section key={g} aria-label={`${g}, ${list.length}`} className="overflow-hidden rounded-lg border border-line bg-raised">
              <h2 className="border-b border-line bg-sunken px-3 py-1.5 text-xs font-semibold text-muted">{g} · {list.length}</h2>
              <table className="hidden w-full table-fixed text-sm md:table">
                <thead className="text-left text-xs text-muted"><tr>
                  <th className="px-3 py-1.5 font-medium">Item</th><th className="w-28 px-3 py-1.5 font-medium">Workpaper</th><th className="w-36 px-3 py-1.5 font-medium">Waits on</th>
                  <th className="w-20 px-3 py-1.5 font-medium">Age</th><th className="w-36 px-3 py-1.5 font-medium"><span className="sr-only">Action</span></th></tr></thead>
                <tbody className="divide-y divide-line">
                  {list.map((i) => (
                    <tr key={`${i.engagementId}:${i.key}`} className="align-top">
                      <td className="px-3 py-2"><ItemTitle i={i} />
                        {byClient && i.area && <p className="text-xs text-muted">{i.area}</p>}</td>
                      <td className="px-3 py-2"><RefLink i={i} /></td>
                      <td className="px-3 py-2"><WaitsOn i={i} /></td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-muted">{age(i.createdAt)}</td>
                      <td className="px-3 py-2 text-right"><ItemAction item={i} open={open} engagementName={byClient ? names.get(i.engagementId) : undefined} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="divide-y divide-line md:hidden">
                {list.map((i) => (
                  <li key={`${i.engagementId}:${i.key}`} className="space-y-1.5 px-3 py-2.5 text-sm">
                    <ItemTitle i={i} />
                    <p className="flex flex-wrap gap-x-3 text-xs text-muted"><RefLink i={i} /><span>Waits on <WaitsOn i={i} /></span><span>{age(i.createdAt)}</span></p>
                    <ItemAction item={i} open={open} engagementName={byClient ? names.get(i.engagementId) : undefined} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </div>
      )}
      {drawer}
    </div>
  );
}

const HISTORY = "history";

/** The item, its kind and the engine's detail; a flagged issue or a tailoring review links to its full explanation, and an issue
 * being worked shows its status and the latest note. */
function ItemTitle({ i }: { i: AttentionItem }) {
  const to = detailHref(i);
  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {to ? <Link to={to} className="font-medium text-ink underline decoration-line underline-offset-2 hover:text-brand">{i.title}</Link> : <span className="font-medium">{i.title}</span>}
        <SubkindChip subkind={i.subkind} />
        <AttentionIssueChip issue={i.issue} />
      </div>
      {i.detail && <p className="text-xs text-muted">{i.detail}</p>}
      {i.issue?.lastNote && i.issue.status !== "open" && <p className="text-xs text-muted">Latest: {i.issue.lastNote}{i.issue.lastBy ? ` — ${i.issue.lastBy}` : ""}</p>}
    </>
  );
}

function RefLink({ i }: { i: AttentionItem }) {
  if (!i.ref) return <span className="text-xs text-muted">—</span>;
  return <Link className="font-mono text-xs text-brand hover:underline" to={`/e/${i.engagementId}/wp/${encodeURIComponent(i.ref)}`}>{i.ref}</Link>;
}
function WaitsOn({ i }: { i: AttentionItem }) {
  if (i.waitsOn.kind === "client") return <span className="text-client">Client{i.waitsOn.name ? ` · ${i.waitsOn.name}` : ""}</span>;
  if (i.waitsOn.kind === "agent") return <Who name="agent" />;
  return <Who name={i.waitsOn.name} />;
}

/** Route wrappers. */
export function EngagementActions() { const { id = "" } = useParams(); return <ActionCenter scope={{ kind: "engagement", id }} />; }
export function ClientActions() { const { clientId = "" } = useParams(); return <ActionCenter scope={{ kind: "client", clientId: decodeURIComponent(clientId) }} />; }
export function FirmActions() { return <ActionCenter scope={{ kind: "firm" }} />; }
